import { useState, useEffect } from "react";

type AlertVariant = "info" | "warning" | "error" | "success";

interface AlertProps {
  message: string;
  variant?: AlertVariant;
  dismissible?: boolean;
}

const variantStyles: Record<AlertVariant, {
  container: string;
  icon: string;
  iconSymbol: string;
}> = {
  info: {
    container: "bg-blue-50 border border-blue-200 text-blue-800",
    icon: "bg-blue-100 text-blue-600",
    iconSymbol: "ℹ",
  },
  warning: {
    container: "bg-amber-50 border border-amber-200 text-amber-800",
    icon: "bg-amber-100 text-amber-600",
    iconSymbol: "⚠",
  },
  error: {
    container: "bg-red-50 border border-red-200 text-red-800",
    icon: "bg-red-100 text-red-600",
    iconSymbol: "✕",
  },
  success: {
    container: "bg-green-50 border border-green-200 text-green-800",
    icon: "bg-green-100 text-green-600",
    iconSymbol: "✓",
  },
};

export default function Alert({ message, variant = "info", dismissible = false }: AlertProps) {
  const [visible, setVisible] = useState(true);
  const [mounted, setMounted] = useState(false);
  const styles = variantStyles[variant];

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  return (
    <div
      className={`fixed top-24 right-4 flex items-start gap-3 rounded-xl px-4 py-3.5 shadow-lg max-w-sm w-full z-50 sm:left-auto sm:translate-x-0 left-1/2 -translate-x-1/2 transition-all duration-2000 ease-out ${styles.container} ${
        mounted ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
      }`}
    >
      <div
        className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${styles.icon}`}
      >
        {styles.iconSymbol}
      </div>
      <p className="flex-1 text-sm leading-snug">{message}</p>
      {dismissible && (
        <button
          onClick={() => setVisible(false)}
          className="flex-shrink-0 opacity-50 hover:opacity-100 text-lg leading-none transition-opacity"
          aria-label="Zavřít"
        >
          ×
        </button>
      )}
    </div>
  );
}